import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import { Api } from './api'
import * as fileOperations from './file-operations'
import * as management from './management'
import type { TypeParams } from './types'

const DEFAULT_QUANTITY = 20

/**
 * Provides documents and document actions for the selected Agent-specific or
 * Shared knowledge-base audience.
 */
@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams> {
  /** Document operations and their state scoped to this collection. */
  public readonly fileOperations: fileOperations.Feature

  /** Management inventory and browser workflow scoped to this collection. */
  public readonly management: management.Feature

  constructor() {
    const api = ngCore.inject(Api)

    super({
      api,
      identify: file => file.key,
      name: ec.Name.Predefined.AgentKnowledgeBaseFile,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(() => ({
            rowConfig: ({ entity: file }) => {
              const downloading = this.fileOperations.selectors
                .isDownloading(file.key)
                .asSignal()
              const deleting =
                this.fileOperations.selectors.isDeleting.asSignal()
              const canOfferDelete =
                file.knowledgeBaseId !==
                ents.core.agent.KnowledgeBase.SharedScoped
              return {
                iconsConfigs: [
                  {
                    class: 'atn-icon atn-download atn-blue',
                    disabled: downloading,
                    loading: downloading,
                    onClick: () =>
                      this.fileOperations.actions.download
                        .from(`Admin.KnowledgeBases.File-${file.key}.Download`)
                        .dispatch(file),
                    title: 'Download document',
                  },
                  ...(canOfferDelete
                    ? [
                        {
                          class: 'atn-icon atn-trash atn-red',
                          disabled: !file.canDelete || deleting,
                          onClick: () =>
                            this.fileOperations.actions.selectForDeletion
                              .from(
                                `Admin.KnowledgeBases.File-${file.key}.Delete.Selected`,
                              )
                              .dispatch({ fileKey: file.key }),
                          title: file.canDelete
                            ? 'Delete document'
                            : 'This document is read-only in the Admin Center.',
                        },
                      ]
                    : []),
                ],
              }
            },
          })),
        ],
        currentList: {
          quantity: {
            get: quantity => quantity ?? DEFAULT_QUANTITY,
          },
        },
      },
    })

    this.fileOperations = new fileOperations.Feature(this, api)
    this.management = new management.Feature(this, api)
    this.management.actions.load.request
      .from('Admin.KnowledgeBases.Initialized')
      .dispatch()
    this.management.actions.loadTenants.request
      .from('Admin.KnowledgeBases.Initialized')
      .dispatch()
  }
}
